import React, { useEffect } from 'react';
import styled from 'styled-components';
import { LayoutColors } from '@src/core/theme';
import { Scheduling } from '@src/core/buyerApiClient';
import { useAppDispatch, useAppSelector, useTranslate } from '@src/core/hooks';
import { loadSeatMap, schedulingSeatMapSelector, schedulingSeatMapLoadingSelector } from '@src/stores/product';
import Loading from '@src/components/ui2/Loading';
import { useAllocationStatus } from '@src/domains/products/hooks/useAllocationStatus';

interface Props {
  scheduling: Scheduling;
  timeMinutes?: number;
}

const SeatsUnavailableAlert = (props: Props) => {
  const { scheduling, timeMinutes } = props;
  const { i18nElement } = useTranslate();

  const dispatch = useAppDispatch();
  const seatMap = useAppSelector(schedulingSeatMapSelector(scheduling.id, timeMinutes));
  const isSeatMapLoading = useAppSelector(schedulingSeatMapLoadingSelector(scheduling.id, timeMinutes));
  const allocationStatus = useAllocationStatus({ scheduling, timeMinutes });

  useEffect(() => {
    // Fallback for WithDateOnly products: loadSeatMapValidationForHours only handles
    // products with time slots (WithDateAndTime). For WithDateOnly, we need to load
    // the seatMap directly here since there are no scheduleTimes to validate.
    if (scheduling?.requiresSeatSelection && !seatMap) {
      dispatch(loadSeatMap(scheduling.id, timeMinutes));
    }
  }, [dispatch, scheduling.id, scheduling?.requiresSeatSelection, timeMinutes, seatMap]);

  // Show loading only when seat selection is required and seatMap is loading
  if (scheduling?.requiresSeatSelection && isSeatMapLoading) {
    return <Loading size={40} color={'#262ba6'} />;
  }

  if (allocationStatus.success) {
    return null;
  }

  return (
    <AlertContainer>
      <p>
        {allocationStatus.missingTariffs > 0
          ? i18nElement('adder_seats_missing_tariffs_unavailable_error', {
              missing: String(allocationStatus.missingTariffs),
            })
          : i18nElement('adder_seats_unavailable_error')}
      </p>
    </AlertContainer>
  );
};

const AlertContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  padding: 16px;
  font-weight: bold;
  border-radius: 4px;
  background-color: ${LayoutColors.errorLight};

  p {
    color: ${LayoutColors.errorDark};
  }
`;

export default SeatsUnavailableAlert;
